/**
 * useSchedulingLinks Hook
 *
 * Manages scheduling links and bookings via the API.
 * Provides CRUD operations for links and booking management.
 */

import { useState, useEffect, useCallback } from 'react';
import { api, SchedulingLinkDetail, SchedulingLinkInput, BookingDetail } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

export function useSchedulingLinks() {
  const [schedulingLinks, setSchedulingLinks] = useState<SchedulingLinkDetail[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.listSchedulingLinks();
      setSchedulingLinks(result.schedulingLinks);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to load scheduling links',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const createLink = useCallback(async (input: SchedulingLinkInput) => {
    const created = await api.createSchedulingLink(input);
    setSchedulingLinks((prev) => [created, ...prev]);
    return created;
  }, []);

  const updateLink = useCallback(async (id: string, input: Partial<SchedulingLinkInput>) => {
    const updated = await api.updateSchedulingLink(id, input);
    setSchedulingLinks((prev) => prev.map((l) => (l.id === id ? updated : l)));
    return updated;
  }, []);

  const deleteLink = useCallback(async (id: string) => {
    await api.deleteSchedulingLink(id);
    setSchedulingLinks((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const fetchBookings = useCallback(
    async (linkId: string, status?: string): Promise<BookingDetail[]> => {
      const result = await api.listSchedulingLinkBookings(linkId, status);
      return result.bookings;
    },
    [],
  );

  const cancelBooking = useCallback(async (linkId: string, bookingId: string) => {
    await api.cancelBookingByHost(linkId, bookingId);
  }, []);

  return {
    schedulingLinks,
    loading,
    fetchLinks,
    createLink,
    updateLink,
    deleteLink,
    fetchBookings,
    cancelBooking,
  };
}
